import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Grid, Container, Box } from '@mui/material';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import ImageSection from './components/ImageSection';
import ProductSection from './components/ProductSection';
import ProductDetails from './components/ProductDetails';
import MissingPage from '../../components/shared/errorMessages/MissingPage';
import { apiFilterProducts } from '../../api/apiMultiFilter';
import { captureAnalytics } from '../../utilities/Analytics';
import posthog from '../../utils/posthog'

interface StockMaster {
  diameter?: string;
  width?: string;
  length_cm?: string;
  colour?: string;
  description_1?: string;
  design?: string;
  range?: string;
  brand?: string;
  composition?: string;
  group_desc?: string;
  on_hand?: number;
  cut_price_ex_vat?: string;
  price_retail_inc_vat?: string;
  item_group?: string;
  stock_link?: number;
  cleaning?: string;
  c_weight?: string;
  repeat_t?: string;
  repeat_h?: string;
  hanging_instructions?: string;
  railroaded?: string;
  application?: string;
  martindale?: string;
  po_delivery_date?: string;
  [key: string]: any;
}

interface Product {
  id?: string;
  sku?: string;
  primaryImageUrl?: string;
  additionalImages?: any[];
  long_description?: string;
  cleaning_instructions?: any[];
  standards?: any[];
  disambiguating_description?: string;
  [key: string]: any;
}

interface ProductItem {
  Product?: Product;
  StockMaster?: StockMaster;
  sku?: string;
  diameter?: string;
  width?: string;
  length_cm?: string;
  colour?: string;
  description_1?: string;
  design?: string;
  range?: string;
  brand?: string;
  composition?: string;
  group_desc?: string;
  on_hand?: number;
  cut_price_ex_vat?: string;
  price_retail_inc_vat?: string;
  item_group?: string;
  stock_link?: number;
  primaryImageUrl?: string;
  additionalImages?: any[];
  long_description?: string;
  cleaning_instructions?: any[];
  standards?: any[];
  disambiguating_description?: string;
  [key: string]: any;
}

function ProductPage() {
  // URL parameters
  const { item_group, collection, design, colour } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetSize = searchParams.get('targetSize');
  const targetProduct = searchParams.get('targetProduct');

  // 🔥 ADDED: Missing state variables from trade repo
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<ProductItem>();
  const [selectedImagePreviewIndex, setSelectedImagePreviewIndex] = useState<number>(0);
  const [width, setWidth] = useState('');
  const [lengthcm, setLengthCM] = useState('');
  const [diameter, setDiameter] = useState('');
  const [activeImageStep, setActiveImageStep] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const queryClient = useQueryClient();

  // Build query key
  const queryKey = useMemo(() => {
    const baseKey = [
      'product-filter',
      item_group,
      design,
      targetProduct
    ];

    if (targetProduct && targetSize) {
      if (diameter) {
        return [...baseKey, 'diameter', diameter];
      } else if (width && lengthcm) {
        return [...baseKey, 'dimensions', width, lengthcm];
      }
    }

    return baseKey;
  }, [item_group, design, colour, targetProduct, width, lengthcm, diameter, targetSize]);

  // Main query
  const {
    isSuccess,
    isError,
    data: productData,
    isLoading,
  } = useQuery(
    queryKey,
    async () => {
      console.log('Consumer ProductPage query running');

      // Use multifilter API to get ALL products for this design
      const multifilterProducts = await apiFilterProducts({
        filters: {
          item_group: item_group,
          design: design,
          range: collection, // Add collection filter
        },
        filter_fields: ["item_group", "design", "range", "colour"],
        main_filter_field: null,
        selection_fields: null,
        sort_by_setting: "Alphabetical A-Z",
        off_setter: { start: 0, end: 1000 },
        only_published: true,
        only_promo: false,
        only_in_stock: false,
        limited: false
      });

      console.log('🔥 Consumer API Request Filters:', {
        item_group: item_group,
        design: design,
        range: collection
      });

      console.log('Raw multifilter response:', multifilterProducts);

      // Extract data from API response
      const getDataItems = (response: any) => {
        if (!response) return [];

        if (response.success && response.products && Array.isArray(response.products)) {
          return response.products;
        }

        if (response.data && Array.isArray(response.data)) {
          return response.data;
        }

        if (Array.isArray(response)) {
          return response.slice(0, -1);
        }

        return [];
      };

      const productItems = getDataItems(multifilterProducts);
      console.log('All products for design:', productItems);

      return {
        data: productItems,
        selection_fields: {}
      };
    },
    {
      enabled: !!item_group && !!collection && !!design,
      staleTime: 60000,
      cacheTime: 60000,
      onSuccess: (data) => {
        console.log('Query onSuccess called with:', data);

        const items = data?.data || [];
        console.log('Items extracted:', items);

        if (!Array.isArray(items) || items.length === 0) {
          console.log('No items found or items is not an array');
          return;
        }

        // Handle initial load with target product
        if (isInitialLoad && targetProduct && targetSize) {
          console.log('Handling initial load with target:', { targetProduct, targetSize });

          const targetIndex = items.findIndex((item: ProductItem) => {
            const itemWidth = (item.StockMaster?.width || item.width || '').trim();
            const itemLength = (item.StockMaster?.length_cm || item.length_cm || '').trim();
            const itemDiameter = (item.StockMaster?.diameter || item.diameter || '').trim();
            const itemSku = item.Product?.sku || item.sku;

            const currentSize = itemDiameter && itemDiameter !== '0cm'
              ? itemDiameter
              : `${itemWidth}x${itemLength}`;

            const sizeMatch = currentSize === targetSize;
            const skuMatch = itemSku === targetProduct;

            console.log('Checking item:', { itemSku, currentSize, sizeMatch, skuMatch });

            return skuMatch && sizeMatch;
          });

          console.log('Target index found:', targetIndex);

          if (targetIndex !== -1) {
            const item = items[targetIndex];
            if (!item) return;

            Promise.resolve().then(() => {
              setCurrentIndex(targetIndex);
              setSelectedImagePreviewIndex(targetIndex);
              setActiveImageStep(0);

              const itemDiameter = item.StockMaster?.diameter || item.diameter;
              const itemWidth = item.StockMaster?.width || item.width;
              const itemLength = item.StockMaster?.length_cm || item.length_cm;

              if (itemDiameter && itemDiameter !== '0cm') {
                setDiameter(itemDiameter.trim());
                setWidth('');
                setLengthCM('');
              } else {
                const width = itemWidth?.replace('cm', '').trim() || '';
                const length = itemLength?.replace('cm', '').trim() || '';
                setWidth(width);
                setLengthCM(length);
                setDiameter('');
              }

              setTimeout(() => {
                setIsInitialLoad(false);
              }, 100);
            });
          } else {
            setIsInitialLoad(false);
          }
        } else if (isInitialLoad) {
          setIsInitialLoad(false);
        }

        // Set initial color if provided in URL
        if (colour !== undefined) {
          const initialColourIndex = items.findIndex(
            (item: any) => (item?.StockMaster?.colour || item?.colour) === colour
          );
          if (initialColourIndex !== -1) {
            setCurrentIndex(initialColourIndex);
            setSelectedImagePreviewIndex(initialColourIndex);
          }
        }
      }
    }
  );

  // Cache invalidation effect
  useEffect(() => {
    if (sessionStorage.getItem('fromFilterComponent') === 'true') {
      console.log('Invalidating cache due to filter component navigation');
      queryClient.invalidateQueries(['product-filter']);
      sessionStorage.removeItem('fromFilterComponent');
    }
  }, [location.pathname, queryClient]);

  useEffect(() => {
    if (location.pathname.includes('/design/')) {
      sessionStorage.setItem('fromFilterComponent', 'true');
    }

    return () => {
      if (window.history.state?.idx !== undefined &&
        window.history.state.idx > 0 &&
        location.pathname.includes('/design/')) {
        // Going back - keep flags intact
      } else {
        // Navigating forward - clean up
        sessionStorage.removeItem('fromFilterComponent');
        sessionStorage.removeItem('multifilter_active');
        sessionStorage.removeItem('multifilter_scroll_position');
        sessionStorage.removeItem('multifilter_item_group');
      }
    };
  }, [location.pathname]);

  const productDataItems = productData?.data || [];

  // Helper function to get property from either nested or flat structure
  const getProperty = (item: ProductItem, prop: string): any => {
    if (item && item[prop] !== undefined) {
      return item[prop];
    }

    if (item?.StockMaster && item.StockMaster[prop] !== undefined) {
      return item.StockMaster[prop];
    }

    if (item?.Product && item.Product[prop] !== undefined) {
      return item.Product[prop];
    }

    return undefined;
  };

  const symbols = productDataItems.length > 0
    ? (getProperty(productDataItems[currentIndex], 'symbols') || []).filter(
      (symbol: any) => !!symbol?.file_url
    )
    : [];

  // Build product details list
  const productDetailsList =
    productDataItems && productDataItems.length > 0
      ? [
        {
          key: 'Product Code (SKU)',
          value: getProperty(productDataItems[currentIndex], 'sku'),
        },
        {
          key: 'Description',
          value: getProperty(productDataItems[currentIndex], 'description_1'),
        },
        {
          key: 'Design',
          value: getProperty(productDataItems[currentIndex], 'design'),
        },
        {
          key: 'Colour',
          value: getProperty(productDataItems[currentIndex], 'colour'),
        },
        {
          key: 'Range',
          value: getProperty(productDataItems[currentIndex], 'range'),
        },
        {
          key: 'Brand',
          value: getProperty(productDataItems[currentIndex], 'brand'),
        },
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Rugs'
          ? [
            {
              key: 'Size',
              value: `${getProperty(productDataItems[currentIndex], 'width')} x ${getProperty(productDataItems[currentIndex], 'length_cm')}`,
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'brand') === 'HAUS'
          ? [
            {
              key: 'Weight',
              value: getProperty(productDataItems[currentIndex], 'c_weight'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Width',
              value: getProperty(productDataItems[currentIndex], 'width'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Roll Dimension',
              value: `${getProperty(productDataItems[currentIndex], 'width')} x ${getProperty(productDataItems[currentIndex], 'length_cm')}`,
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric' ||
        getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Vertical Repeat',
              value: getProperty(productDataItems[currentIndex], 'repeat_t'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper' ||
        getProperty(productDataItems[currentIndex], 'group_desc') === 'Rugs'
          ? [
            {
              key: 'Composition',
              value: getProperty(productDataItems[currentIndex], 'composition'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Wallpaper'
          ? [
            {
              key: 'Installation',
              value: getProperty(productDataItems[currentIndex], 'hanging_instructions'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Horizontal Repeat',
              value: getProperty(productDataItems[currentIndex], 'repeat_h'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Railroaded',
              value: getProperty(productDataItems[currentIndex], 'railroaded'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Composition',
              value: getProperty(productDataItems[currentIndex], 'composition'),
            },
          ]
          : []),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Weight',
              value: getProperty(productDataItems[currentIndex], 'c_weight'),
            },
          ]
          : []),
        ...((() => {
          const cleaningInstructions = getProperty(productDataItems[currentIndex], 'cleaning_instructions');
          const cleaningText = getProperty(productDataItems[currentIndex], 'cleaning');

          // Check if cleaning_instructions exists and has items
          if (Array.isArray(cleaningInstructions) && cleaningInstructions.length > 0) {
            // Remove duplicates based on file_url and text
            const uniqueInstructions = cleaningInstructions.reduce((acc: any[], instruction: any) => {
              const text = instruction.symbol_name || '';
              const imageUrl = instruction.file_url || '';

              // Create a unique key combining text and image
              const uniqueKey = `${text.trim()}-${imageUrl}`;

              // Check if this combination already exists
              const alreadyExists = acc.some((item: any) => {
                const existingText = item.symbol_name || '';
                const existingImage = item.file_url || '';
                const existingKey = `${existingText.trim()}-${existingImage}`;
                return existingKey === uniqueKey;
              });

              if (!alreadyExists) {
                acc.push(instruction);
              }

              return acc;
            }, []);

            // Check if any instructions have images
            const hasImagesInInstructions = uniqueInstructions.some(instruction => instruction.file_url);

            if (hasImagesInInstructions) {
              // Use cleaning_instructions with images
              return [{
                key: 'Care Info',
                value: (
                  <Box>
                    {/* Display images for instructions that have them */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '7px',
                        marginBottom: '10px'
                      }}
                    >
                      {uniqueInstructions.map(
                        (instruction: { file_url?: any; file_name?: string; symbol_name?: string }, index: number) =>
                          instruction.file_url ? (
                            <img
                              key={index}
                              src={instruction.file_url}
                              alt={instruction.file_name || instruction.symbol_name || 'Care instruction'}
                              style={{
                                width: '30px',
                                height: '30px',
                                paddingRight: '0',
                              }}
                              draggable={false}
                              onDragStart={(e) => e.preventDefault()}
                            />
                          ) : null
                      )}
                    </Box>

                    {/* Display text for instructions without images and not hyphens */}
                    <Box>
                      {uniqueInstructions
                        .filter((instruction: any) => {
                          const hasImage = instruction.file_url;
                          const text = instruction.symbol_name;
                          const isValidText = text && text.trim() !== '-' && text.trim() !== '';
                          return !hasImage && isValidText;
                        })
                        .map((instruction: any, index: number, filteredArray: any[]) => (
                          <span key={index}>
                    {instruction.symbol_name}
                            {index < filteredArray.length - 1 ? '; ' : ''}
                  </span>
                        ))}
                    </Box>
                  </Box>
                ),
              }];
            }
          }

          // Fallback to cleaning text field if no images in cleaning_instructions
          if (cleaningText && cleaningText.trim() !== '' && cleaningText.trim() !== '-') {
            return [{
              key: 'Care Info',
              value: <Box>{cleaningText}</Box>,
            }];
          }

          // Return empty array if nothing to show
          return [];
        })()),
        ...(getProperty(productDataItems[currentIndex], 'group_desc') === 'Fabric'
          ? [
            {
              key: 'Martindale Rubs',
              value: getProperty(productDataItems[currentIndex], 'martindale'),
            },
          ]
          : []),
        {
          key: 'Application',
          value: getProperty(productDataItems[currentIndex], 'application'),
        },
        {
          key: 'Comments',
          value: getProperty(productDataItems[currentIndex], 'disambiguating_description') ? (
            <Box>
              {getProperty(productDataItems[currentIndex], 'disambiguating_description')
                .split('\n')
                .filter((line: string) => line.trim() !== '')
                .map((line: string, index: number) => (
                  <span key={index}>
                    {line}
                    {index < getProperty(productDataItems[currentIndex], 'disambiguating_description').split('\n').filter((l: string) => l.trim() !== '').length - 1 && <br />}
                  </span>
                ))}
            </Box>
          ) : 'N/A',
        },
        {
          key: 'Standards',
          value: Array.isArray(getProperty(productDataItems[currentIndex], 'standards')) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
              }}
            >
              {getProperty(productDataItems[currentIndex], 'standards').map(
                (standard: any, index: any) => (
                  <img
                    key={index}
                    src={standard.file_url}
                    alt={standard.file_name}
                    style={{
                      height: '60px',
                      paddingRight: '0',
                    }}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  />
                )
              )}
            </Box>
          ) : null,
        },
      ]
      : [];

  // Effects
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [colour]);

  useEffect(() => {
    captureAnalytics('pageview', location.pathname, `product-${design}`);
  }, [location.pathname, design]);

  useEffect(() => {
    const product = productDataItems[currentIndex];
    if (!product) return;

    console.log('🟢 FIRING view_product', product.sku);

    posthog.capture('view_product', {
      product_code: getProperty(product, 'sku'),
      product_name: getProperty(product, 'design'),
      product_colour: getProperty(product, 'colour'),
      item_group: getProperty(product, 'item_group'),
      group_desc: getProperty(product, 'group_desc'),
      brand: getProperty(product, 'brand'),
      range: getProperty(product, 'range'),
      collection: collection,
      source: 'consumer',
    });
  }, [currentIndex, productDataItems, collection]);

  // Loading and error states
  if (isLoading) return <LoadingScreen />;
  if (isError) return <MissingPage />;

  return (
    <>
      {isSuccess && productDataItems && (
        <>
          <Container maxWidth="xl">
            <Grid
              mt={{ xs: 5, sm: 10 }}
              container
              sx={{
                flexDirection: {
                  xs: 'column',
                  lg: 'row',
                },
              }}
            >
              <ImageSection
                productListData={productDataItems}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                currentSelectedItem={currentSelectedItem}
                selectedImagePreviewIndex={selectedImagePreviewIndex}
                activeImageStep={activeImageStep}
                setActiveImageStep={setActiveImageStep}
              />

              <ProductSection
                productListData={productDataItems} // 🔥 FIXED: Changed from productData to productListData
                productData={productData}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                itemGroup={item_group}
                application={getProperty(productDataItems[currentIndex], 'application')} // 🔥 FIXED: Get from productDataItems instead
                collection={collection}
                design={design}
                colour={colour}
                currentSelectedItem={currentSelectedItem}
                setCurrentSelectedItem={setCurrentSelectedItem}
                setSelectedImagePreviewIndex={setSelectedImagePreviewIndex}
                selectedImagePreviewIndex={selectedImagePreviewIndex}
                // 🔥 ADDED: Missing props
                setWidth={setWidth}
                setLengthCM={setLengthCM}
                setDiameter={setDiameter}
                diameter={diameter}
                width={width}
                lengthCM={lengthcm}
                activeImageStep={activeImageStep}
                setActiveImageStep={setActiveImageStep}
                isInitialLoad={isInitialLoad}
                setIsInitialLoad={setIsInitialLoad}
                userHasInteracted={userHasInteracted}
                setUserHasInteracted={setUserHasInteracted}
              />
            </Grid>

            {symbols.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '10px',
                  width: '100%',
                  // Match ImageSection's own left padding (pl: {lg: '50px'})
                  // so this row's left edge lines up with the image and
                  // with Product Details below.
                  pl: { xs: '0', lg: '50px' },
                  mt: 2,
                  mb: 2,
                }}
              >
                {symbols.map((symbol: any, index: number) => (
                  <Box
                    key={index}
                    component="img"
                    src={symbol.file_url}
                    alt={symbol.file_name}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    sx={{
                      width: '90px',
                      height: '90px',
                      maxWidth: '90px',
                      flex: '0 0 auto',
                      objectFit: 'contain',
                    }}
                  />
                ))}
              </Box>
            )}

            <ProductDetails productDetailsList={productDetailsList} />
          </Container>
        </>
      )}
    </>
  );
}

export default ProductPage;